import { useState, useEffect, useCallback } from 'react';
import type { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { RoleGroupType } from '@server/role-group-interface';
import { SweepRoles } from '../../../hooks/useUserInfo';
import { telemetry } from '../../../telemetry';
import type { MessageResponse } from './InviteUserInput';
import { inviteMessages } from './InviteUserInput';
import { validateEmails } from '../../Automations/helper';

export const getDefaultViewerRole = (roles: UserDefinedRoleGroupsInterface[]) =>
  roles?.find((role) => role.type === RoleGroupType.SYSTEM && role.name === SweepRoles.Viewer);

interface UseInviteFormProps {
  roles: UserDefinedRoleGroupsInterface[];
  onSendInvitationEmail: (email: string, role: string, message: string) => Promise<any>;
  afterSendActions: (isError: boolean, messageResponse: MessageResponse) => Promise<void>;
}

export const useInviteForm = ({
  roles,
  onSendInvitationEmail,
  afterSendActions,
}: UseInviteFormProps) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isInvitationSending, setIsInvitationSending] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserDefinedRoleGroupsInterface | undefined>(
    getDefaultViewerRole(roles),
  );

  useEffect(() => {
    setSelectedRole(getDefaultViewerRole(roles));
  }, [roles]);

  const clearState = useCallback(() => {
    setEmail('');
    setMessage('');
    setIsInvitationSending(false);
  }, []);

  const sendInvitationEmail = useCallback(async () => {
    setIsInvitationSending(true);
    try {
      if (!selectedRole?.id) return;
      await onSendInvitationEmail(email, selectedRole.id, message);
      afterSendActions(false, inviteMessages(email));
      clearState();
    } catch (error) {
      telemetry.captureError(error);
      afterSendActions(true, inviteMessages(email));
      setIsInvitationSending(false);
    }
  }, [email, message, selectedRole, onSendInvitationEmail, afterSendActions, clearState]);

  const selectRoleById = useCallback(
    (roleId: string) => {
      setSelectedRole(roles.find((role) => role.id === roleId));
    },
    [roles],
  );

  const isEmailValid = validateEmails([email]);

  return {
    email,
    setEmail,
    message,
    setMessage,
    selectedRole,
    selectRoleById,
    isInvitationSending,
    sendInvitationEmail,
    clearState,
    isEmailValid,
  };
};
